import { getApiEndpoint } from './config.js';
import { checkLogin, checkToken } from './account.js';

/**
 * Get conversations.
 *
 * @param {string} filter - mine or own, based on what should be fetched.
 * @param {int} offset - The offset to get results from.
 * @param {int} limit - The number of conversations to get.
 * @param {object} token - The token to use, for getting conversations.
 * @param {object} history - The history, for redirecting when user is not logged
 * in
 * @param {function} callback - The callback to trigger, after getting
 * conversations.
 */
export function getConversations(filter, offset, limit, token, history, callback) {
  if (!checkToken(token, history)) {
    return;
  }
  var parameters = [];
  if (filter) {
    parameters.push('status=' + encodeURIComponent(filter));
  }
  if (offset) {
    parameters.push('offset=' + encodeURIComponent(offset));
  }
  if (limit) {
    parameters.push('limit=' + encodeURIComponent(limit));
  }

  fetch(getApiEndpoint() + '/conversations?' + parameters.join('&'), {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'X-Session-Id': token.id,
    },
  }).then(function(response) {
    checkLogin(response, history, callback);
  });
}

/**
 * Get open conversations count.
 *
 * @param {object} token - The token to use, for getting conversations.
 * @param {object} history - The history, for redirecting when user is not logged
 * in
 * @param {function} callback - The callback to trigger, after getting
 * conversations.
 */
export function getOpenConversationsCount(token, history, callback) {
  if (!checkToken(token, history)) {
    return;
  }

  fetch(getApiEndpoint() + '/conversations/open-count', {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'X-Session-Id': token.id,
    },
  }).then(function(response) {
    checkLogin(response, history, callback);
  });
}

/**
 * Get a specific conversation.
 *
 * @param {int} conversationId - The id of the conversation.
 * @param {object} token - The token to use, for getting conversation.
 * @param {object} history - The history, for redirecting when user is not logged
 * in
 * @param {function} callback - The callback to trigger, after getting
 * conversation.
 */
export function getConversation(conversationId, token, history, callback) {
  if (!checkToken(token, history)) {
    return;
  }

  fetch(getApiEndpoint() + '/conversations/' + conversationId, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'X-Session-Id': token.id,
    },
  }).then(function(response) {
    checkLogin(response, history, callback);
  });
}

/**
 * Get messages for a conversation.
 *
 * @param {int} conversationId - The id of the conversation.
 * @param {int} offset - The offset to get results from.
 * @param {int} limit - The number of messages to get.
 * @param {object} token - The token to use, for getting conversation.
 * @param {object} history - The history, for redirecting when user is not logged
 * in
 * @param {function} callback - The callback to trigger, after getting
 * conversation.
 */
export function getMessages(conversationId, offset, limit, token, history, callback) {
  if (!checkToken(token, history)) {
    return;
  }
  var parameters = [];
  if (offset) {
    parameters.push('offset=' + encodeURIComponent(offset));
  }
  if (limit) {
    parameters.push('limit=' + encodeURIComponent(limit));
  }

  fetch(getApiEndpoint() + '/conversations/' + conversationId + '/messages?' + parameters.join('&'), {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'X-Session-Id': token.id,
    },
  }).then(function(response) {
    checkLogin(response, history, callback);
  });
}

/**
 * Waive a conversation.
 *
 * @param {int} conversationId - The id of the conversation.
 * @param {object} token - The token to use, for getting conversation.
 * @param {object} history - The history, for redirecting when user is not logged
 * in
 * @param {function} callback - The callback to trigger, after getting
 * conversation.
 */
export function waiveConversation(conversationId, token, history, callback) {
  if (!checkToken(token, history)) {
    return;
  }

  fetch(getApiEndpoint() + '/conversations/' + conversationId, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      'X-Session-Id': token.id,
    },
    body: JSON.stringify([{
      op: 'clear',
      fields: ['advisor_id'],
    }]),
  }).then(function(response) {
    checkLogin(response, history, callback);
  });
}

/**
 * Take a conversation.
 *
 * @param {int} conversationId - The id of the conversation.
 * @param {object} token - The token to use, for getting conversation.
 * @param {object} history - The history, for redirecting when user is not logged
 * in
 * @param {function} callback - The callback to trigger, after getting
 * conversation.
 */
export function takeConversation(conversationId, token, history, callback) {
  if (!checkToken(token, history)) {
    return;
  }

  fetch(getApiEndpoint() + '/conversations/' + conversationId, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      'X-Session-Id': token.id,
    },
    body: JSON.stringify([{
      op: 'set',
      fields: {
        advisor_id: token.user_id,
      },
    }]),
  }).then(function(response) {
    checkLogin(response, history, callback);
  });
}

/**
 * Close a conversation.
 *
 * @param {int} conversationId - The id of the conversation.
 * @param {bool} sendClose - Whether or not to send closing SMS.
 * @param {object} token - The token to use, for getting conversation.
 * @param {object} history - The history, for redirecting when user is not logged
 * in
 * @param {function} callback - The callback to trigger, after getting
 * conversation.
 */
export function closeConversation(conversationId, sendClose, token, history, callback) {
  if (!checkToken(token, history)) {
    return;
  }

  fetch(getApiEndpoint() + '/conversations/' + conversationId, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      'X-Session-Id': token.id,
    },
    body: JSON.stringify([{
      op: 'set',
      fields: {
        status: 'closed',
        doNotSendClosedMessage: !sendClose,
      },
    }]),
  }).then(function(response) {
    checkLogin(response, history, callback);
  });
}

/**
 * Create a message, for sending out.
 *
 * @param {int} conversationId - The id of the conversation.
 * @param {string} message - The message.
 * @param {object} token - The token to use, for getting conversation.
 * @param {object} history - The history, for redirecting when user is not logged
 * in
 * @param {function} callback - The callback to trigger, after getting
 * conversation.
 */
export function sendMessage(conversationId, message, token, history, callback) {
  if (!checkToken(token, history)) {
    return;
  }

  fetch(getApiEndpoint() + '/conversations/' + conversationId + '/messages', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Session-Id': token.id,
    },
    body: JSON.stringify({
      message: message,
      direction: 'out',
    }),
  }).then(function(response) {
    checkLogin(response, history, callback);
  });
}

/**
 * Save a conversation.
 *
 * @param {int} conversationId - The id of the conversation.
 * @param {array} fields - The fields to save.
 * @param {object} token - The token to use, for saving user.
 * @param {object} history - The history, for redirecting when user is not logged
 * in
 * @param {function} callback - The callback to trigger, after saving user.
 */
export function saveConversation(conversationId, fields, token, history, callback) {
  if (!checkToken(token, history)) {
    return;
  }

  var set = {};
  var clear = [];
  Object.keys(fields).forEach((fieldName) => {
    if (fields[fieldName] === null || fields[fieldName] === "") {
      clear.push(fieldName);
    } else {
      set[fieldName] = fields[fieldName];
    }
  });

  fetch(getApiEndpoint() + '/conversations/' + conversationId, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      'X-Session-Id': token.id,
    },
    body: JSON.stringify([
      {
        op: 'set',
        fields: set,
      },
      {
        op: 'clear',
        fields: clear,
      }
    ]),
  }).then(function(response) {
    checkLogin(response, history, callback);
  });
}

/**
 * Export conversations.
 *
 * @param {int} startTime - The start time, for the export.
 * @param {int} endTime - The end time, for the export.
 * @param {string} status - The status, for the export.
 * @param {object} token - The token to use, for saving user.
 * @param {object} history - The history, for redirecting when user is not logged
 * in
 */
export function exportConversations(startTime, endTime, status, token, history) {
  if (!checkToken(token, history)) {
    return;
  }
  var parameters = [];
  if (startTime) {
    parameters.push('start=' + encodeURIComponent(startTime));
  }
  if (endTime) {
    parameters.push('end=' + encodeURIComponent(endTime));
  }
  if (status) {
    parameters.push('status=' + encodeURIComponent(status));
  }
  parameters.push('session-id=' + encodeURIComponent(token.id));

  var url = getApiEndpoint() + '/conversations/export?' + parameters.join('&');

  window.open(url,'_blank');
}



// WEBPACK FOOTER //
// ./src/utility/conversation.js